import React from 'react';
import getConfig from 'next/config';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { Row, Col, Divider } from 'antd';
import { NextSeo } from 'next-seo';
import BasicLayout from 'layout/Basic';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import { convertProps } from 'lib/parseAPIResponse';
import SectionTitle from 'components/Section/SectionTitle';
import Pagination from 'components/Pagination';
import Subscription from 'containers/Subscription';
import { ExpandNavibox, HalfPage, Rectangle, SkyscraperMobile, Wideboard } from 'components/Ads';
import Section from 'components/Section';
import Newsletter from 'containers/Newsletter';
import WhitePaperItem from 'components/List/WhitePaperItem';
import GalleryBlock from 'components/GalleryBlock';
import VideoBlock from 'components/VideoBlock';
import { EventsSection } from 'components/EventsSection';
interface Props {
    whitePapers: Paginated<WhitePaper>;
    latestIssue: Issue;
    events: Occurrence[];
    eventsPatronate: Occurrence[];
    singleGallery: Gallery;
    singleVideo: Video;
    ads: PageAds;
}

export const WhitePaperIndexPage = (props: Props): React.JSX.Element => {
    const router = useRouter();
    const { whitePapers, latestIssue, events, eventsPatronate, singleGallery, singleVideo, ads } =
        props;

    const onChange = (page) => {
        router.push(`?page=${page}`);
    };

    const { name } = getConfig().publicRuntimeConfig;
    const { rectangle, skyscraper, halfpage, navibox, wideboardBottom } = ads;
    return (
        <BasicLayout ads={ads}>
            <NextSeo title={`White Papers | ${name}`} />
            <Row gutter={16}>
                <Content>
                    <Row>
                        <Col span={24}>
                            <SectionTitle title="White papers" />
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        {whitePapers.items.map((item, i) => {
                            if (i === 3) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <WhitePaperItem $whitePaper={item} />
                                        <Row justify="center">
                                            <Col xs={24} sm={12}>
                                                <SkyscraperMobile
                                                    id={skyscraper[0]}
                                                    margin="12px auto 12px auto"
                                                />
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <SkyscraperMobile
                                                    id={skyscraper[1]}
                                                    margin="12px auto 0 auto"
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                );
                            }
                            if (i === 7) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <WhitePaperItem $whitePaper={item} />
                                        <ExpandNavibox id={navibox[0]} margin="25px auto" />
                                    </Col>
                                );
                            }
                            if (i === 13) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <WhitePaperItem $whitePaper={item} />
                                        <ExpandNavibox id={navibox[1]} margin="25px auto" />
                                    </Col>
                                );
                            }
                            return (
                                <Col span={24} key={item.seoSlug}>
                                    <WhitePaperItem $whitePaper={item} />
                                </Col>
                            );
                        })}
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Pagination
                                results={whitePapers.results}
                                currentPage={whitePapers.currentPage}
                                onChange={onChange}
                                next={whitePapers.next}
                                prev={whitePapers.prev}
                            />
                        </Col>
                    </Row>
                    <Divider />
                    <Section gutter={[16, 32]} justify="center">
                        <Col xs={24} md={20}>
                            <Newsletter />
                        </Col>
                    </Section>
                    <Divider />
                    <Col span={24}>
                        <EventsSection events={events} eventsPatronate={eventsPatronate} />
                    </Col>
                </Content>
                <Sider>
                    <Row>
                        <Col span={24}>
                            <Rectangle id={rectangle[0]} margin="12px auto 12px auto" />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[0]} margin="12px auto 12px auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[1]} margin="12px auto 12px auto" />
                        </Col>
                        {(rectangle.length > 0 || halfpage.length > 0) && <Divider />}
                        {singleGallery && (
                            <Col span={24}>
                                <GalleryBlock gallery={singleGallery} />
                            </Col>
                        )}
                        <Col span={24}>
                            <Rectangle id={rectangle[2]} margin="12px auto 12px auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[3]} margin="12px auto 12px auto" />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[1]} margin="12px auto 12px auto" />
                        </Col>
                        {(rectangle.length > 2 || halfpage.length > 1) && <Divider />}
                        {singleVideo && (
                            <>
                                <Divider />
                                <Col span={24}>
                                    <VideoBlock video={singleVideo} />
                                </Col>
                            </>
                        )}
                        <Col span={24}>
                            <Rectangle id={rectangle[4]} margin="12px auto 12px auto" />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[2]} margin="12px auto 12px auto" />
                        </Col>
                    </Row>
                </Sider>
            </Row>
            <Row gutter={16}>
                <Content>
                    <ExpandNavibox id={navibox[2]} margin="35px auto 0 auto" />
                </Content>
                <Sider>
                    <Rectangle id={rectangle[5]} margin="35px auto 0 auto" />
                </Sider>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Subscription issue={latestIssue} />
                </Col>
            </Row>
            <Section>
                <Col span={24}>
                    <Section>
                        <Col span={24}>
                            <Wideboard id={wideboardBottom[0]} />
                        </Col>
                    </Section>
                </Col>
            </Section>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
    const page = typeof query.page === 'string' ? parseInt(query.page) : 1;
    const { apiUrl } = getConfig().publicRuntimeConfig;
    return Promise.all([
        fetch(`${apiUrl}/public/api/white-papers,${page}`),
        fetch(`${apiUrl}/public/api/naszadsik?path=white-papers`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        const ads = await res2.json();

        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default WhitePaperIndexPage;
